/**
 * Entity tracking panel — Phase 2 implementation.
 * Search, list, detail view, create, and watch entities.
 * Renders into the overlay panel system.
 */

import { eventBus } from '@/core/events';
import { entityStore } from '@/intelligence/entities/entity-store';
import type {
  Entity,
  EntityType,
  EntityQuery,
  Relationship,
} from '@/intelligence/entities/types';
import { escapeHtml } from '@/utils/sanitize';

export interface EntityPanelConfig {
  id: 'entity';
  label: string;
  icon: string;
  visible: boolean;
}

export const ENTITY_PANEL_CONFIG: EntityPanelConfig = {
  id: 'entity',
  label: 'Entities',
  icon: 'users',
  visible: true,
};

// ---------------------------------------------------------------------------
// Type icons and colors
// ---------------------------------------------------------------------------

const ENTITY_TYPE_ICONS: Record<EntityType, string> = {
  PERSON: '\u{1F464}',
  ORGANIZATION: '\u{1F3E2}',
  LOCATION: '\u{1F4CD}',
  ASSET: '\u2693',
  EVENT: '\u26A1',
  INDICATOR: '\u{1F4CB}',
  SOURCE: '\u{1F4F0}',
};

const ENTITY_TYPE_COLORS: Record<EntityType, string> = {
  PERSON: '#ff8844',
  ORGANIZATION: '#ff4444',
  LOCATION: '#44ff44',
  ASSET: '#00d4ff',
  EVENT: '#ff44aa',
  INDICATOR: '#aa44ff',
  SOURCE: '#44ffcc',
};

const WATCH_STATUS_LABELS: Record<string, string> = {
  none: '',
  watched: '\u{1F441}',
  priority: '\u{1F534}',
};

// ---------------------------------------------------------------------------
// Panel class
// ---------------------------------------------------------------------------

export class EntityPanel {
  private container: HTMLElement;
  private searchInput: HTMLInputElement | null = null;
  private typeFilter: HTMLSelectElement | null = null;
  private listContainer: HTMLElement | null = null;
  private detailContainer: HTMLElement | null = null;
  private createFormContainer: HTMLElement | null = null;
  private cleanupFns: (() => void)[] = [];
  private currentView: 'list' | 'detail' | 'create' = 'list';
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    this.container = document.createElement('div');
    this.container.className = 'entity-panel';
    this.container.style.cssText = `
      display: flex; flex-direction: column; height: 100%;
      font-family: var(--font-sans, Inter, system-ui, sans-serif);
      color: var(--text-primary, #e2e8f0);
    `;

    this.buildPanel();

    // Listen for entity events
    this.cleanupFns.push(
      eventBus.on('entity:created', () => this.refreshList()),
      eventBus.on('entity:updated', () => this.refreshList()),
      eventBus.on('entity:deleted', () => {
        this.showListView();
      }),
      eventBus.on('entity:selected', (entity) => this.showDetail(entity)),
    );
  }

  get element(): HTMLElement {
    return this.container;
  }

  destroy(): void {
    for (const fn of this.cleanupFns) fn();
    this.cleanupFns.length = 0;
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
  }

  // ── Layout ──

  private buildPanel(): void {
    // Header bar
    const header = document.createElement('div');
    header.style.cssText = `
      display: flex; align-items: center; gap: 8px; padding: 10px 12px;
      border-bottom: 1px solid var(--border-color, #334155);
    `;

    const title = document.createElement('span');
    title.style.cssText = 'font-weight: 600; font-size: 14px; flex: 1;';
    title.textContent = 'Entities';
    header.appendChild(title);

    const createBtn = document.createElement('button');
    createBtn.style.cssText = this.buttonStyle();
    createBtn.textContent = '+ New';
    createBtn.title = 'Create entity manually';
    createBtn.addEventListener('click', () => this.showCreateForm());
    header.appendChild(createBtn);

    this.container.appendChild(header);

    // Search + filter row
    const searchRow = document.createElement('div');
    searchRow.style.cssText = 'display: flex; gap: 6px; padding: 8px 12px;';

    this.searchInput = document.createElement('input');
    this.searchInput.type = 'text';
    this.searchInput.placeholder = 'Search entities\u2026';
    this.searchInput.style.cssText = `
      flex: 1; background: var(--bg-secondary, #1e293b); border: 1px solid var(--border-color, #334155);
      border-radius: 4px; padding: 6px 8px; color: var(--text-primary, #e2e8f0);
      font-size: 12px; outline: none;
    `;
    this.searchInput.addEventListener('input', () => this.debouncedSearch());
    searchRow.appendChild(this.searchInput);

    this.typeFilter = document.createElement('select');
    this.typeFilter.style.cssText = `
      background: var(--bg-secondary, #1e293b); border: 1px solid var(--border-color, #334155);
      border-radius: 4px; padding: 4px 6px; color: var(--text-primary, #e2e8f0);
      font-size: 12px;
    `;
    const allOption = document.createElement('option');
    allOption.value = '';
    allOption.textContent = 'All types';
    this.typeFilter.appendChild(allOption);
    for (const type of ['PERSON', 'ORGANIZATION', 'LOCATION', 'ASSET', 'EVENT', 'INDICATOR', 'SOURCE'] as EntityType[]) {
      const opt = document.createElement('option');
      opt.value = type;
      opt.textContent = type;
      this.typeFilter.appendChild(opt);
    }
    this.typeFilter.addEventListener('change', () => this.refreshList());
    searchRow.appendChild(this.typeFilter);

    this.container.appendChild(searchRow);

    // Content area (switches between list, detail, and create views)
    this.listContainer = document.createElement('div');
    this.listContainer.style.cssText = 'flex: 1; overflow-y: auto; padding: 0 12px 12px;';
    this.container.appendChild(this.listContainer);

    this.detailContainer = document.createElement('div');
    this.detailContainer.style.cssText = 'flex: 1; overflow-y: auto; padding: 0 12px 12px; display: none;';
    this.container.appendChild(this.detailContainer);

    this.createFormContainer = document.createElement('div');
    this.createFormContainer.style.cssText = 'flex: 1; overflow-y: auto; padding: 0 12px 12px; display: none;';
    this.container.appendChild(this.createFormContainer);

    // Initial load
    this.refreshList();
  }

  // ── Search ──

  private debouncedSearch(): void {
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => this.refreshList(), 250);
  }

  private async refreshList(): Promise<void> {
    if (this.currentView !== 'list') this.showListView();

    const query: EntityQuery = { limit: 100 };
    const text = this.searchInput?.value.trim();
    if (text) query.text = text;
    const typeVal = this.typeFilter?.value;
    if (typeVal) query.type = typeVal as EntityType;

    try {
      const entities = await entityStore.search(query);
      this.renderList(entities);
    } catch (err) {
      console.error('[Sentinel] Entity search failed:', err);
      if (this.listContainer) {
        this.listContainer.innerHTML = `<div style="color: var(--text-muted, #64748b); padding: 16px; font-size: 12px;">
          Database not ready. Entities will be available after initialization.
        </div>`;
      }
    }
  }

  private showListView(): void {
    this.currentView = 'list';
    if (this.listContainer) this.listContainer.style.display = '';
    if (this.detailContainer) this.detailContainer.style.display = 'none';
    if (this.createFormContainer) this.createFormContainer.style.display = 'none';
  }

  // ── List rendering ──

  private renderList(entities: Entity[]): void {
    if (!this.listContainer) return;
    this.listContainer.innerHTML = '';

    if (entities.length === 0) {
      const empty = document.createElement('div');
      empty.style.cssText = 'color: var(--text-muted, #64748b); padding: 24px 0; text-align: center; font-size: 12px;';
      empty.textContent = 'No entities found. Create one or run an investigation.';
      this.listContainer.appendChild(empty);
      return;
    }

    for (const entity of entities) {
      this.listContainer.appendChild(this.renderEntityCard(entity));
    }
  }

  private renderEntityCard(entity: Entity): HTMLElement {
    const card = document.createElement('div');
    card.style.cssText = `
      display: flex; align-items: center; gap: 8px; padding: 8px;
      border-radius: 4px; cursor: pointer; margin-bottom: 4px;
      border: 1px solid var(--border-color, #334155);
      transition: background 150ms;
    `;
    card.addEventListener('mouseenter', () => { card.style.background = 'var(--bg-hover, #1e293b)'; });
    card.addEventListener('mouseleave', () => { card.style.background = ''; });
    card.addEventListener('click', () => {
      eventBus.emit('entity:selected', entity);
    });

    // Type icon
    const icon = document.createElement('span');
    icon.style.cssText = `
      font-size: 18px; width: 28px; height: 28px; display: flex;
      align-items: center; justify-content: center; border-radius: 4px;
      background: ${ENTITY_TYPE_COLORS[entity.type]}22;
    `;
    icon.textContent = ENTITY_TYPE_ICONS[entity.type] ?? '\u25CF';
    card.appendChild(icon);

    // Name + type
    const info = document.createElement('div');
    info.style.cssText = 'flex: 1; min-width: 0;';
    const nameEl = document.createElement('div');
    nameEl.style.cssText = 'font-size: 13px; font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;';
    nameEl.textContent = entity.name;
    info.appendChild(nameEl);

    const meta = document.createElement('div');
    meta.style.cssText = 'font-size: 11px; color: var(--text-muted, #64748b);';
    meta.textContent = `${entity.type} \u00B7 ${entity.confidence > 0 ? (entity.confidence * 100).toFixed(0) + '%' : 'unrated'}`;
    info.appendChild(meta);
    card.appendChild(info);

    // Watch status badge
    if (entity.watchStatus !== 'none') {
      const badge = document.createElement('span');
      badge.style.cssText = 'font-size: 14px;';
      badge.textContent = WATCH_STATUS_LABELS[entity.watchStatus] ?? '';
      badge.title = entity.watchStatus === 'priority' ? 'Priority watch' : 'Watched';
      card.appendChild(badge);
    }

    // Confidence badge
    if (entity.confidence > 0) {
      const conf = document.createElement('span');
      conf.style.cssText = `
        font-size: 10px; padding: 2px 5px; border-radius: 3px;
        background: ${entity.confidence >= 0.7 ? '#22c55e33' : entity.confidence >= 0.4 ? '#eab30833' : '#ef444433'};
        color: ${entity.confidence >= 0.7 ? '#22c55e' : entity.confidence >= 0.4 ? '#eab308' : '#ef4444'};
        font-weight: 600;
      `;
      conf.textContent = `${(entity.confidence * 100).toFixed(0)}%`;
      card.appendChild(conf);
    }

    return card;
  }

  // ── Detail view ──

  private async showDetail(entity: Entity): Promise<void> {
    this.currentView = 'detail';
    if (this.listContainer) this.listContainer.style.display = 'none';
    if (this.createFormContainer) this.createFormContainer.style.display = 'none';
    if (this.detailContainer) {
      this.detailContainer.style.display = '';
      this.detailContainer.innerHTML = '';
    }

    // Back button
    const backRow = document.createElement('div');
    backRow.style.cssText = 'display: flex; align-items: center; gap: 8px; padding: 8px 0;';
    const backBtn = document.createElement('button');
    backBtn.style.cssText = this.buttonStyle();
    backBtn.textContent = '\u2190 Back';
    backBtn.addEventListener('click', () => {
      this.showListView();
      this.refreshList();
    });
    backRow.appendChild(backBtn);
    this.detailContainer!.appendChild(backRow);

    // Entity header
    const headerEl = document.createElement('div');
    headerEl.style.cssText = 'display: flex; align-items: center; gap: 10px; margin-bottom: 12px;';
    const iconEl = document.createElement('span');
    iconEl.style.cssText = `
      font-size: 28px; width: 40px; height: 40px; display: flex;
      align-items: center; justify-content: center; border-radius: 6px;
      background: ${ENTITY_TYPE_COLORS[entity.type]}22;
    `;
    iconEl.textContent = ENTITY_TYPE_ICONS[entity.type] ?? '\u25CF';
    headerEl.appendChild(iconEl);

    const headerInfo = document.createElement('div');
    headerInfo.style.cssText = 'flex: 1;';
    const nameH = document.createElement('div');
    nameH.style.cssText = 'font-size: 16px; font-weight: 600;';
    nameH.textContent = entity.name;
    headerInfo.appendChild(nameH);

    const typeLine = document.createElement('div');
    typeLine.style.cssText = 'font-size: 12px; color: var(--text-muted, #64748b);';
    typeLine.textContent = `${entity.type.charAt(0).toUpperCase() + entity.type.slice(1)} \u00B7 Confidence: ${(entity.confidence * 100).toFixed(0)}%`;
    headerInfo.appendChild(typeLine);
    headerEl.appendChild(headerInfo);
    this.detailContainer!.appendChild(headerEl);

    // Watch toggle
    const watchRow = document.createElement('div');
    watchRow.style.cssText = 'display: flex; gap: 6px; margin-bottom: 12px;';
    for (const status of ['none', 'watched', 'priority'] as const) {
      const btn = document.createElement('button');
      const isActive = entity.watchStatus === status;
      btn.style.cssText = `
        ${this.buttonStyle()}
        ${isActive ? 'background: var(--accent-primary, #3b82f6); color: white;' : ''}
      `;
      btn.textContent = status === 'none' ? 'Unwatched' : status === 'watched' ? '\u{1F441} Watch' : '\u{1F534} Priority';
      btn.addEventListener('click', async () => {
        try {
          const updated = await entityStore.update(entity.id, { watchStatus: status });
          this.showDetail(updated);
        } catch (err) {
          console.error('[Sentinel] Failed to update watch status:', err);
        }
      });
      watchRow.appendChild(btn);
    }
    this.detailContainer!.appendChild(watchRow);

    // Description
    if (entity.description) {
      this.detailContainer!.appendChild(this.createSection('Description', escapeHtml(entity.description)));
    }

    // Aliases
    if (entity.aliases.length > 0) {
      this.detailContainer!.appendChild(
        this.createSection('Aliases', entity.aliases.map(a => escapeHtml(a)).join(', ')),
      );
    }

    // Tags
    if (entity.tags.length > 0) {
      const tagSection = this.createSection('Tags', '');
      const tagContainer = tagSection.querySelector('.section-content')!;
      tagContainer.innerHTML = '';
      for (const tag of entity.tags) {
        const tagEl = document.createElement('span');
        tagEl.style.cssText = `
          display: inline-block; padding: 2px 6px; margin: 2px; border-radius: 3px;
          background: var(--bg-secondary, #1e293b); font-size: 11px;
          border: 1px solid var(--border-color, #334155);
        `;
        tagEl.textContent = tag;
        tagContainer.appendChild(tagEl);
      }
      this.detailContainer!.appendChild(tagSection);
    }

    // Properties
    const propKeys = Object.keys(entity.properties);
    if (propKeys.length > 0) {
      const propHtml = propKeys
        .map(k => `<div style="font-size: 12px; margin-bottom: 4px;"><strong>${escapeHtml(k)}:</strong> ${escapeHtml(String(entity.properties[k]))}</div>`)
        .join('');
      this.detailContainer!.appendChild(this.createSection('Properties', propHtml, true));
    }

    // Provenance
    if (entity.provenance.sourceUrl) {
      const provHtml = `
        <div style="font-size: 12px; margin-bottom: 4px;"><strong>Source:</strong> ${escapeHtml(entity.provenance.sourceUrl)}</div>
        <div style="font-size: 12px; margin-bottom: 4px;"><strong>Quote:</strong> ${escapeHtml(entity.provenance.extractionQuote)}</div>
        <div style="font-size: 12px; margin-bottom: 4px;"><strong>Confidence:</strong> ${(entity.provenance.confidence * 100).toFixed(0)}%</div>
      `;
      this.detailContainer!.appendChild(this.createSection('Provenance', provHtml, true));
    }

    // Corroboration / NATO Rating
    if (entity.corroboration.count > 0) {
      const corrobHtml = `
        <div style="font-size: 12px; margin-bottom: 4px;"><strong>NATO Rating:</strong> ${escapeHtml(entity.corroboration.natoRating)}</div>
        <div style="font-size: 12px; margin-bottom: 4px;"><strong>Sources:</strong> ${entity.corroboration.sources.map(s => escapeHtml(s)).join(', ')}</div>
        <div style="font-size: 12px; margin-bottom: 4px;"><strong>Count:</strong> ${entity.corroboration.count}</div>
      `;
      this.detailContainer!.appendChild(this.createSection('Corroboration', corrobHtml, true));
    }

    // Notes
    if (entity.notes.length > 0) {
      const notesHtml = entity.notes
        .map(n => `<div style="font-size: 12px; margin-bottom: 4px; padding: 4px 6px; background: var(--bg-secondary, #1e293b); border-radius: 3px;">${escapeHtml(n)}</div>`)
        .join('');
      this.detailContainer!.appendChild(this.createSection('Notes', notesHtml, true));
    }

    // Relationships
    try {
      const rels = await entityStore.getRelationships(entity.id);
      if (rels.length > 0) {
        this.detailContainer!.appendChild(this.renderRelationships(entity.id, rels));
      }
    } catch {
      // DB may not be ready
    }

    // Timeline
    try {
      const timeline = await entityStore.getTimeline(entity.id);
      if (timeline.length > 0) {
        this.detailContainer!.appendChild(this.renderTimeline(timeline));
      }
    } catch {
      // DB may not be ready
    }

    // Investigate button (WP-S13.4)
    const invRow = document.createElement('div');
    invRow.style.cssText = 'padding: 8px 0;';
    const invBtn = document.createElement('button');
    invBtn.style.cssText = `${this.buttonStyle()} color: #00b4d8; border-color: #00b4d833;`;
    invBtn.textContent = 'Investigate';
    invBtn.addEventListener('click', () => {
      eventBus.emit('investigation:request', { query: entity.name, tier: 'STANDARD' });
    });
    invRow.appendChild(invBtn);
    this.detailContainer!.appendChild(invRow);

    // Delete button
    const deleteRow = document.createElement('div');
    deleteRow.style.cssText = 'padding: 16px 0; border-top: 1px solid var(--border-color, #334155); margin-top: 12px;';
    const deleteBtn = document.createElement('button');
    deleteBtn.style.cssText = `${this.buttonStyle()} color: #ef4444; border-color: #ef444433;`;
    deleteBtn.textContent = 'Delete Entity';
    deleteBtn.addEventListener('click', async () => {
      if (!confirm(`Delete entity "${entity.name}"? This will also remove all its relationships.`)) return;
      try {
        await entityStore.delete(entity.id);
      } catch (err) {
        console.error('[Sentinel] Failed to delete entity:', err);
      }
    });
    deleteRow.appendChild(deleteBtn);
    this.detailContainer!.appendChild(deleteRow);
  }

  private renderRelationships(entityId: string, rels: Relationship[]): HTMLElement {
    const section = this.createSection('Relationships', '', true);
    const content = section.querySelector('.section-content')!;
    content.innerHTML = '';

    for (const rel of rels) {
      const row = document.createElement('div');
      row.style.cssText = 'font-size: 12px; margin-bottom: 6px; display: flex; align-items: center; gap: 6px;';

      const dirLabel = rel.sourceEntityId === entityId ? '\u2192' : '\u2190';
      const otherId = rel.sourceEntityId === entityId ? rel.targetEntityId : rel.sourceEntityId;

      row.innerHTML = `
        <span style="color: var(--text-muted);">${dirLabel}</span>
        <span style="font-weight: 500;">${escapeHtml(rel.type)}</span>
        <span style="color: var(--text-muted); font-size: 11px;">${escapeHtml(otherId.slice(0, 8))}\u2026</span>
        <span style="margin-left: auto; font-size: 10px; color: var(--text-muted);">${(rel.confidence * 100).toFixed(0)}%</span>
      `;
      content.appendChild(row);
    }

    return section;
  }

  private renderTimeline(events: import('@/intelligence/entities/types').TimelineEvent[]): HTMLElement {
    const section = this.createSection('Timeline', '', true);
    const content = section.querySelector('.section-content')!;
    content.innerHTML = '';

    for (const ev of events) {
      const row = document.createElement('div');
      row.style.cssText = 'font-size: 12px; margin-bottom: 6px; display: flex; gap: 8px;';
      row.innerHTML = `
        <span style="color: var(--text-muted); white-space: nowrap; font-size: 11px;">${escapeHtml(ev.timestamp.slice(0, 10))}</span>
        <span>${escapeHtml(ev.description)}</span>
      `;
      content.appendChild(row);
    }

    return section;
  }

  // ── Create form ──

  private showCreateForm(): void {
    this.currentView = 'create';
    if (this.listContainer) this.listContainer.style.display = 'none';
    if (this.detailContainer) this.detailContainer.style.display = 'none';
    if (this.createFormContainer) {
      this.createFormContainer.style.display = '';
      this.createFormContainer.innerHTML = '';
    }

    const form = this.createFormContainer!;

    // Back button
    const backBtn = document.createElement('button');
    backBtn.style.cssText = `${this.buttonStyle()} margin: 8px 0;`;
    backBtn.textContent = '\u2190 Cancel';
    backBtn.addEventListener('click', () => {
      this.showListView();
      this.refreshList();
    });
    form.appendChild(backBtn);

    const heading = document.createElement('div');
    heading.style.cssText = 'font-weight: 600; font-size: 14px; margin-bottom: 12px;';
    heading.textContent = 'Create Entity';
    form.appendChild(heading);

    // Name
    const nameInput = this.createFormField('Name', 'text', 'Entity name');
    form.appendChild(nameInput.wrapper);

    // Type
    const typeSelect = document.createElement('select');
    typeSelect.style.cssText = this.inputStyle();
    for (const t of ['PERSON', 'ORGANIZATION', 'LOCATION', 'ASSET', 'EVENT', 'INDICATOR', 'SOURCE'] as EntityType[]) {
      const opt = document.createElement('option');
      opt.value = t;
      opt.textContent = t;
      typeSelect.appendChild(opt);
    }
    const typeWrapper = document.createElement('div');
    typeWrapper.style.cssText = 'margin-bottom: 8px;';
    const typeLabel = document.createElement('label');
    typeLabel.style.cssText = 'font-size: 11px; color: var(--text-muted, #64748b); display: block; margin-bottom: 3px;';
    typeLabel.textContent = 'Type';
    typeWrapper.appendChild(typeLabel);
    typeWrapper.appendChild(typeSelect);
    form.appendChild(typeWrapper);

    // Description
    const descInput = this.createFormField('Description', 'text', 'Optional description');
    form.appendChild(descInput.wrapper);

    // Tags
    const tagsInput = this.createFormField('Tags', 'text', 'Comma-separated tags');
    form.appendChild(tagsInput.wrapper);

    // Submit
    const submitBtn = document.createElement('button');
    submitBtn.style.cssText = `${this.buttonStyle()} background: var(--accent-primary, #3b82f6); color: white; margin-top: 8px; width: 100%;`;
    submitBtn.textContent = 'Create Entity';
    submitBtn.addEventListener('click', async () => {
      const name = nameInput.input.value.trim();
      if (!name) {
        nameInput.input.style.borderColor = '#ef4444';
        return;
      }

      const tags = tagsInput.input.value.trim()
        ? tagsInput.input.value.split(',').map(t => t.trim()).filter(Boolean)
        : [];

      try {
        submitBtn.disabled = true;
        submitBtn.textContent = 'Creating\u2026';

        const ts = new Date().toISOString();
        await entityStore.create({
          type: typeSelect.value as EntityType,
          name,
          aliases: [],
          description: descInput.input.value.trim() || undefined,
          properties: {},
          provenance: {
            sourceUrl: '',
            extractionQuote: '',
            collectorId: 'manual',
            collectedAt: ts,
            confidence: 0,
          },
          corroboration: {
            count: 0,
            sources: [],
            natoRating: 'F6',
          },
          sources: [],
          relationships: [],
          firstSeen: ts,
          lastSeen: ts,
          confidence: 0,
          tags,
          notes: [],
          watchStatus: 'none',
        });

        this.showListView();
        this.refreshList();
      } catch (err) {
        console.error('[Sentinel] Failed to create entity:', err);
        submitBtn.disabled = false;
        submitBtn.textContent = 'Create Entity';
      }
    });
    form.appendChild(submitBtn);
  }

  // ── Helpers ──

  private createSection(title: string, content: string, isHtml = false): HTMLElement {
    const section = document.createElement('div');
    section.style.cssText = 'margin-bottom: 12px;';
    const label = document.createElement('div');
    label.style.cssText = 'font-size: 11px; color: var(--text-muted, #64748b); font-weight: 600; text-transform: uppercase; margin-bottom: 4px;';
    label.textContent = title;
    section.appendChild(label);

    const body = document.createElement('div');
    body.className = 'section-content';
    body.style.cssText = 'font-size: 12px;';
    if (isHtml) {
      body.innerHTML = content;
    } else {
      body.textContent = content;
    }
    section.appendChild(body);
    return section;
  }

  private createFormField(label: string, type: string, placeholder: string): { wrapper: HTMLElement; input: HTMLInputElement } {
    const wrapper = document.createElement('div');
    wrapper.style.cssText = 'margin-bottom: 8px;';
    const labelEl = document.createElement('label');
    labelEl.style.cssText = 'font-size: 11px; color: var(--text-muted, #64748b); display: block; margin-bottom: 3px;';
    labelEl.textContent = label;
    wrapper.appendChild(labelEl);

    const input = document.createElement('input');
    input.type = type;
    input.placeholder = placeholder;
    input.style.cssText = this.inputStyle();
    wrapper.appendChild(input);

    return { wrapper, input };
  }

  private buttonStyle(): string {
    return `
      background: transparent; border: 1px solid var(--border-color, #334155);
      border-radius: 4px; padding: 4px 10px; cursor: pointer;
      color: var(--text-primary, #e2e8f0); font-size: 12px;
    `;
  }

  private inputStyle(): string {
    return `
      width: 100%; box-sizing: border-box;
      background: var(--bg-secondary, #1e293b); border: 1px solid var(--border-color, #334155);
      border-radius: 4px; padding: 6px 8px; color: var(--text-primary, #e2e8f0);
      font-size: 12px; outline: none;
    `;
  }
}

// ---------------------------------------------------------------------------
// Factory functions (used by panel manager)
// ---------------------------------------------------------------------------

let _panelInstance: EntityPanel | null = null;

/** Create the entity panel element for the panel manager. */
export function createEntityPanel(): HTMLElement {
  _panelInstance = new EntityPanel();
  return _panelInstance.element;
}

/** Mount the entity panel into a container. */
export function mountEntityPanel(container: HTMLElement): void {
  if (!_panelInstance) {
    _panelInstance = new EntityPanel();
  }
  container.appendChild(_panelInstance.element);
}

/** Unmount the entity panel. */
export function unmountEntityPanel(): void {
  if (_panelInstance) {
    _panelInstance.destroy();
    _panelInstance.element.remove();
    _panelInstance = null;
  }
}
